import { getCookieValue } from "~/utils/cookies";

/**
 * Last-known cart line-item count, persisted in a cookie (SFCC-11599).
 *
 * The cart is client-fetched (BasketProvider's keyed `useFetcher`), so on a COLD
 * load `useBasket()` is `undefined` and the cart loading skeleton has no live
 * count while it renders. A cookie (unlike localStorage) is sent with the
 * request, so the cart loader can read it and seed the skeleton on the very
 * first SERVER paint — the skeleton matches the cart immediately (0 → empty-cart
 * layout, N → N rows) with no flash from an unknown→known transition (which is what a
 * client-only read caused: "no-items" then the real count, or "multiple items"
 * then empty). BasketProvider rewrites it whenever the basket resolves;
 * first-ever visit (no cookie) → `undefined` → empty-cart layout skeleton.
 */
const CART_COUNT_COOKIE = "hec_cart_count";
/** A guest's Save for Later list length; the list itself stays in localStorage (COM-294). */
export const SFL_COUNT_COOKIE = "hec_sfl_count";
const MAX_AGE_SECONDS = 60 * 60 * 24 * 30; // 30 days

/** Client-side write — called by BasketProvider once the basket resolves. */
export const writePersistedCartCount = (
  count: number,
  cookie = CART_COUNT_COOKIE,
): void => {
  if (typeof document === "undefined") return;
  document.cookie = `${cookie}=${count}; path=/; max-age=${MAX_AGE_SECONDS}; SameSite=Lax`;
};

/**
 * Read the persisted count from a Cookie header — works on the server
 * (`request.headers.get("cookie")`) and the client (`document.cookie`). Returns
 * `undefined` when the cookie is absent or not a valid non-negative integer.
 */
export const readPersistedCartCount = (
  cookieHeader: string | null | undefined,
  cookie = CART_COUNT_COOKIE,
): number | undefined => {
  const raw = getCookieValue(cookieHeader, cookie);
  if (raw == null) return undefined;
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : undefined;
};
